import { HPG_ENABLED_CLIENTS } from '../constants/HPGConfig';

const config = {
  SESSION_TIME: {
    TIME_TO_WARNING: 5,
    MAX_SESSION_LENGTH: 30,
    INTERVAL_TIME: 60000,
  },
  REFRESH_TOKEN: {
    REFRESH_WINDOW: 30,
    INERTVAL_TIME: 60000,
  },
};
type ValidRole = {
name: string;
memberId: string;
permission: string[];
};

// Helper to safely get hostname
const getHostname = (): string => {
  return typeof window !== 'undefined' ? window.location?.hostname : '';
};

/**
 * Securely checks if the hostname is a valid HealthTrust domain.
 * Validates that the hostname is either exactly 'healthtrust.optum.com' 
 * or a subdomain ending with '.healthtrust.optum.com'.
 * This prevents domain spoofing attacks like 'malicious-healthtrust.optum.com.evil.com'.
 * 
 * @param hostname - The hostname to validate
 * @returns true if the hostname is a valid HealthTrust domain, false otherwise
 */
export const isHealthTrustDomain = (hostname: string): boolean => {
  if (!hostname) {
    return false;
  }
  
  const trustedDomain = 'healthtrust.optum.com';
  
  // Check if hostname is exactly the trusted domain
  if (hostname === trustedDomain) {
    return true;
  }
  
  // Check if hostname is a valid subdomain (ends with .healthtrust.optum.com)
  // This ensures 'api.healthtrust.optum.com' matches but 'healthtrust.optum.com.evil.com' does not
  if (hostname.endsWith('.' + trustedDomain)) {
    return true;
  }
  
  return false;
};

/**
 * Get the current base URL based on the hostname the user is accessing from.
 * This ensures routing URLs (redirect_uri, navigation) match the current domain.
 * 
 * @returns The base URL for the current domain from environment variables
 */
export const getBaseUrl = (): string => {
  const isHealthTrust = isHealthTrustDomain(getHostname());
  return isHealthTrust 
    ? process.env.REACT_APP_HEALTHTRUST_BASE_URL || '' 
    : process.env.REACT_APP_BASE_URL || '';
};

/**
 * Get the current API URL based on the hostname the user is accessing from.
 * This ensures API calls go to the same domain for cookie/session matching.
 * 
 * @returns The API URL for the current domain from environment variables
 */
export const getApiUrl = (): string => {
  const isHealthTrust = isHealthTrustDomain(getHostname());
  return isHealthTrust 
    ? process.env.REACT_APP_HEALTHTRUST_API_URL || '' 
    : process.env.REACT_APP_API_URL || '';
};

export interface Permissions {
  [key: string]: boolean;
}

// Types for the return object
export interface ClientDomainAction {
  type: 'redirect' | 'filter' | 'none';
  url?: string;
  filteredPermissions?: Permissions | ValidRole[];
}

const getHPGConfig = (() => {
  let cachedHPGConfig: any = null;
  let cacheInitialized = false;

  return () => {
    if (cacheInitialized) {
      return cachedHPGConfig;
    }
    
    try {
      const currentEnv = process.env.REACT_APP_ENV;
      cachedHPGConfig = HPG_ENABLED_CLIENTS?.find(
        (config: any) => config.env === currentEnv
      );
      cacheInitialized = true;
      return cachedHPGConfig;
    } catch (error) {
      console.warn('Unable to load HPG configuration:', error);
      cacheInitialized = true;
      return null;
    }
  };
})();

/**
 * Handles client domain redirection logic based on client types (HPG vs non-HPG)
 * and current domain. This function checks all member IDs in the permissions list
 * and determines the appropriate action based on client types and domain matching.
 * 
 * @param permissionsList - The formatted permissions object with member IDs as keys
 * @returns ClientDomainAction object indicating what action to take
 */
export const handleClientDomainRedirection = (
  permissionsList: any
): ClientDomainAction => {
  if (!permissionsList || Object.keys(permissionsList).length === 0) {
    return { type: 'none' };
  }

  const hostname = getHostname();
  if (!hostname) {
    return { type: 'none' };
  }

  const hpgConfig = getHPGConfig();
  if (!hpgConfig) {
    return { type: 'none' };
  }

  const hpgEnabledClients = hpgConfig.clientIds || [];
  const fssocode = typeof localStorage !== 'undefined' ? localStorage.getItem('fssocode') : null;
  const allMemberIds = Object.keys(permissionsList);
  const isHealthTrustDomainCurrent = isHealthTrustDomain(hostname);

  // Categorize clients - single pass through all members
  let hpgCount = 0;
  let nonHpgCount = 0;
  const hpgClients: string[] = [];
  const nonHpgClients: string[] = [];

  for (const memberId of allMemberIds) {
    if (hpgEnabledClients.includes(memberId)) {
      hpgClients.push(memberId);
      hpgCount++;
    } else {
      nonHpgClients.push(memberId);
      nonHpgCount++;
    }
  }

  const allAreHPG = hpgCount > 0 && nonHpgCount === 0;
  const allAreNonHPG = nonHpgCount > 0 && hpgCount === 0;
  const isMixed = hpgCount > 0 && nonHpgCount > 0;

  // Case 1: All are HPG clients
  if (allAreHPG) {
    if (isHealthTrustDomainCurrent) {
      return { type: 'none' };
    }
    const healthTrustBaseUrl = process.env.REACT_APP_HEALTHTRUST_BASE_URL;
    const redirectUrl = fssocode ? `${healthTrustBaseUrl}?fssocode=${fssocode}` : healthTrustBaseUrl;
    return { type: 'redirect', url: redirectUrl };
  }

  // Case 2: All are non-HPG clients
  if (allAreNonHPG) {
    if (!isHealthTrustDomainCurrent) {
      return { type: 'none' };
    }
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const redirectUrl = fssocode ? `${baseUrl}?fssocode=${fssocode}` : baseUrl;
    return { type: 'redirect', url: redirectUrl };
  }

  // Case 3: Mixed clients - filter based on current domain
  if (isMixed) {
    const filteredPermissions = { ...permissionsList };
    const clientsToRemove = isHealthTrustDomainCurrent ? nonHpgClients : hpgClients;
    
    clientsToRemove.forEach(memberId => {
      delete filteredPermissions[memberId];
    });

    return { type: 'filter', filteredPermissions };
  }

  return { type: 'none' };
};

export const handleClientDomainFiltering = (
  validRoles: ValidRole[],
  
): ClientDomainAction => {
  if (!validRoles?.length) return { type: "none" };

  const hostname = getHostname();
  const hpgConfig = getHPGConfig();

  if (!hostname || !hpgConfig) return { type: "none" };

  const hpgSet = new Set(hpgConfig.clientIds ?? []);
  const isHealthTrust = isHealthTrustDomain(hostname);
  const fssocode = typeof localStorage !== 'undefined' ? localStorage.getItem('fssocode') : null;

  // Partition roles in one pass
  const hpgRoles: ValidRole[] = [];
  const nonHpgRoles: ValidRole[] = [];

  for (const role of validRoles) {
    (hpgSet.has(role.memberId) ? hpgRoles : nonHpgRoles).push(role);
  }

  const allHpg = hpgRoles.length > 0 && nonHpgRoles.length === 0;
  const allNonHpg = nonHpgRoles.length > 0 && hpgRoles.length === 0;
  const mixed = hpgRoles.length > 0 && nonHpgRoles.length > 0;

  const buildUrl = (base?: string) => {
  if (!base) return undefined;
  return fssocode? `${base}?fssocode=${fssocode}`: base;
  };

  // Case 1: All HPG
  if (allHpg) {
    if (isHealthTrust) return { type: "none" };
    return {
    type: "redirect",
    url: buildUrl(process.env.REACT_APP_HEALTHTRUST_BASE_URL),
    };
  }

  // Case 2: All non-HPG
  if (allNonHpg) {
    if (!isHealthTrust) return { type: "none" };
    return {
    type: "redirect",
    url: buildUrl(process.env.REACT_APP_BASE_URL),
    };
  }

  // Case 3: Mixed
  if (mixed) {
    const toRemove = isHealthTrust ? nonHpgRoles : hpgRoles;
    const removeSet = new Set(toRemove.map(r => r.memberId));

    return {
      type: "filter",
      filteredPermissions: validRoles.filter(
        role => !removeSet.has(role.memberId)
      ),
    };
  }

  return { type: "none" };
  };

export default config;
